import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

const redirectToLogin = () => {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (window.location.pathname !== '/login') {
    localStorage.setItem('postLoginRedirect', currentPath);
    window.location.href = '/login';
  }
};

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Token expired/invalid or access denied -> force re-auth with redirect back
      localStorage.removeItem('token');
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

export const uploadPaper = async (formData) => {
    const response = await api.post(`/papers/upload`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

/**
 * Process uploaded document and extract information using AI
 * @param {FormData} formData - FormData containing the manuscript file
 * @returns {Promise<Object>} Extracted paper information
 */
export const processUploadedDocument = async (formData) => {
    const response = await api.post('/papers/process-document', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const makePayment = async (paymentData) => {
    const response = await api.post(`/payment/process`, paymentData);
    return response.data;
};

// Change this function to fetch only user's submissions
export const fetchUserSubmissions = async () => {
  try {  
    const token = localStorage.getItem('token');
    const response = await api.get('/papers/user', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
    
  } catch (error) {
    throw error;
  }
};

// Keep the original function for admin/general use
export const fetchSubmissions = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get('/papers', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// User Management Functions
export const fetchAllUsers = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get('/admin/users', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUserRole = async (userId, role) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.put(`/admin/users/${userId}/role`, 
      { role }, 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.delete(`/admin/users/${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUserProfile = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get('/auth/profile', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const activateAccount = async (token) => {
  try {
    const response = await api.post('/auth/activate', { token });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const resendActivation = async (email) => {
  try {
    const response = await api.post('/auth/resend-activation', { email });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const forgotPassword = async (email) => {
  try {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const resetPassword = async (data) => {
  try {
    const response = await api.post('/auth/reset-password', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUserProfile = async (profileData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.put('/auth/profile', profileData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.put(`/admin/users/${userId}`, 
      userData, 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const resetUserPassword = async (userId, options) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.post(`/admin/users/${userId}/reset-password`, 
      options, 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Conference Management Functions
export const getSingleConference = async (id) => {
  try {
    const response = await api.get(`/conferences/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchAllConferences = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get('/conferences', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createConference = async (conferenceData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.post('/admin/conferences', conferenceData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateConference = async (conferenceId, conferenceData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.put(`/admin/conferences/${conferenceId}`, conferenceData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteConference = async (conferenceId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.delete(`/admin/conferences/${conferenceId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeConferenceFile = async (conferenceId, filename) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.delete(`/admin/conferences/${conferenceId}/files/${filename}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeConferencePhoto = async (conferenceId, filename) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.delete(`/admin/conferences/${conferenceId}/photos/${filename}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// News Management Functions
export const fetchSingleNews = async (id) => {
  try {
    const response = await api.get(`/news/${id}`);  
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchAllNews = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get('/news', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createNews = async (newsData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.post('/admin/news', newsData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateNews = async (newsId, newsData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.put(`/admin/news/${newsId}`, newsData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteNews = async (newsId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.delete(`/admin/news/${newsId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeNewsFile = async (newsId, filename) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.delete(`/admin/news/${newsId}/files/${filename}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches all conferences that have 'Open' status for submissions
 * @returns {Promise<Array>} Array of open conferences
 */
export const getOpenConferences = async () => {
    try {
        const response = await api.get('/conferences/open');
        return response.data;
    } catch (error) {
        console.error('Error fetching open conferences:', error);
        throw error;
    }
};

/**
 * Fetches all conferences that have 'Open' status for submissions
 * @returns {Promise<Array>} Array of open conferences
 */
export const getActiveConferences = async () => {
    try {
        const response = await api.get('/conferences/active');
        return response.data;
    } catch (error) {
        console.error('Error fetching active conferences:', error);
        throw error;
    }
};

/**
 * Fetch public manuscripts for a conference code such as MFC26.
 * Returns accepted papers with at least one confirmed registration.
 */
export const fetchPublicConferenceManuscripts = async (conferenceCode) => {
  try {
    const response = await api.get(`/papers/public/manuscripts/${conferenceCode}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a paper submission by ID
 * @param {string} paperId - The ID of the paper to delete
 * @returns {Promise<Object>} Success response
 */
export const deletePaperSubmission = async (paperId) => {
    try {
        const response = await api.delete(`/papers/${paperId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting paper submission:', error);
        throw error;
    }
};

/**
 * Update a paper submission by its owner (authenticated user)
 * @param {string} paperId - The ID of the paper to update
 * @param {FormData} paperData - FormData containing updated fields/files
 * @returns {Promise<Object>} Success response
 */
export const updateOwnPaperSubmission = async (paperId, paperData) => {
  try {
    const response = await api.put(`/papers/${paperId}`,
      paperData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating paper submission:', error);
    throw error;
  }
};

/**
 * Fetch the latest conference for the home page
 * @returns {Promise<Object>} Latest conference data
 */

export const fetchLatestConference = async () => {
  // Try the dedicated latest endpoint first
  const response = await api.get('/conferences/latest');
  return response.data;
};

/**
 * Fetch the latest news items for the home page
 * @param {number} limit - Number of news items to fetch (default: 4)
 * @returns {Promise<Array>} Array of latest news items
 */
export const fetchLatestNews = async (limit = 4) => {
    // Try the dedicated latest endpoint first
    const response = await api.get(`/news/latest?limit=${limit}`);
    return response.data;
};


// Registration Management Functions
export const getUserRegistrations = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get('/registration/user', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createRegistration = async (registrationData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.post('/registration', registrationData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const initiateRegistrationPayment = async (registrationId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.post(`/registration/${registrationId}/payment/initiate`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const executeRegistrationPayment = async (registrationId, paymentId, payerId) => {
  try {
    const response = await api.get(`/registration/payment/success?registrationId=${registrationId}&paymentId=${paymentId}&PayerID=${payerId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const cancelRegistrationPayment = async (registrationId) => {
  try {
    const response = await api.get(`/registration/payment/cancel?registrationId=${registrationId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createManualRegistration = async (payload) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.post('/registration/admin/manual', payload, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Conference Fees Management
export const updateConferenceFees = async (conferenceId, fees) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.put(`/admin/conferences/${conferenceId}/fees`, 
      fees, 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Header Photo Management
export const setHeaderPhoto = async (conferenceId, filename) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.put(`/admin/conferences/${conferenceId}/header-photo`, 
      { filename }, 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Booklet Management
export const setBookletFile = async (conferenceId, filename) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.put(`/admin/conferences/${conferenceId}/booklet`,
      { filename },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Paper Management Functions
export const getAllPapers = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get('/admin/papers', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPapersByConference = async (conferenceId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get(`/admin/papers/conference/${conferenceId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePaperStatus = async (paperId, status) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.put(`/admin/papers/${paperId}/status`, 
      { status }, 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePaper = async (paperId, paperData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.put(`/admin/papers/${paperId}`, paperData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const regeneratePaperNumbers = async (conferenceId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.post(`/admin/papers/conference/${conferenceId}/regenerate-numbers`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteAdminPaper = async (paperId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.delete(`/admin/papers/${paperId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const downloadAllManuscripts = async (conferenceId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get(`/admin/papers/conference/${conferenceId}/download-all`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};


// Board & Governance Data Functions
export const fetchBoardYears = async () => {
  try {
    const response = await api.get('/board/years');
    return response.data; // Array of BoardYear documents
  } catch (error) {
    throw error;
  }
};

export const fetchPastPresidents = async () => {
  try {
    const response = await api.get('/board/past-presidents');
    return response.data; // Array of PastPresident documents
  } catch (error) {
    throw error;
  }
};

// -------- Admin Board Management (requires admin token) --------
export const upsertBoardYear = async (yearData) => {
  const token = localStorage.getItem('token');
  try {
    const { data } = await api.post('/board/years', yearData, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return data;
  } catch (error) { throw error; }
};

export const deleteBoardYear = async (period) => {
  const token = localStorage.getItem('token');
  try {
    const { data } = await api.delete(`/board/years/${encodeURIComponent(period)}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return data;
  } catch (error) { throw error; }
};

export const bulkUpsertPastPresidents = async (entries) => {
  const token = localStorage.getItem('token');
  try {
    const { data } = await api.post('/board/past-presidents/bulk', entries, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return data;
  } catch (error) { throw error; }
};

export const addPastPresident = async (entry) => {
  const token = localStorage.getItem('token');
  try {
    const { data } = await api.post('/board/past-presidents', entry, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return data;
  } catch (error) { throw error; }
};

export const deletePastPresident = async (id) => {
  const token = localStorage.getItem('token');
  try {
    const { data } = await api.delete(`/board/past-presidents/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return data;
  } catch (error) { throw error; }
};



// PayPal JS SDK & Conference Registration related functions
export async function fetchPayPalClientId() {
  const { data } = await api.get('/payment/config/paypal');
  return data.clientId;
}

// Create a registration and PayPal order
// params: { conferenceId, paperId?, feeType, accompaniedPersonsCount }
export async function createRegistrationOrder({ conferenceId, paperId, feeType, accompaniedPersonsCount, paymentCurrency, accompaniedPersonName }) {
  const payload = { conferenceId, paperId, feeType };
  if (typeof accompaniedPersonsCount === 'number') {
    payload.accompaniedPersonsCount = accompaniedPersonsCount; // 0 or 1
  }
  if (accompaniedPersonName) {
    payload.accompaniedPersonName = accompaniedPersonName;
  }
  if (paymentCurrency) {
    payload.paymentCurrency = paymentCurrency;
  }
  const { data } = await api.post('/registration/order', payload);
  return data; // { orderId, intentToken }
}

// Capture a PayPal order for an existing registration
export async function captureRegistrationOrder({ registrationId, orderId, intentToken }) {
  const payload = { orderId };
  if (registrationId) payload.registrationId = registrationId;
  if (intentToken) payload.intentToken = intentToken;
  const { data } = await api.post('/registration/capture', payload);
  return data; // { message, status, registrationId }
}

// Fetch current user's registrations
export async function fetchUserRegistrations() {
  const { data } = await api.get('/registration/user');
  return data;
}

// (Optional) fetch registrations for a paper (future enhancement)
export async function fetchPaperRegistrations(paperId) {
  const { data } = await api.get('/registration/user?paperId=' + paperId);
  return data;
}


// Export the configured axios instance as default
export default api;